import { icon } from '../../../shared/icons.js';

/**
 * ft-dropdown
 * A simple dropdown menu for navigation and actions.
 *
 * Usage:
 *   Slotted items:
 *   <ft-dropdown label="Actions">
 *     <ft-dropdown-item value="edit">Edit</ft-dropdown-item>
 *     <ft-dropdown-item value="delete">Delete</ft-dropdown-item>
 *   </ft-dropdown>
 *
 *   Or with options attribute:
 *   <ft-dropdown
 *     options='[{"value":"a","label":"Option A"},{"value":"b","label":"Option B"}]'
 *     value="a">
 *   </ft-dropdown>
 *
 *   Custom trigger (e.g., kebab menu):
 *   <ft-dropdown align="right">
 *     <button slot="trigger" class="kebab-btn">⋮</button>
 *     <ft-dropdown-item value="edit">Edit</ft-dropdown-item>
 *     <ft-dropdown-item value="delete">Delete</ft-dropdown-item>
 *   </ft-dropdown>
 *
 * Slots:
 *   - trigger: Custom trigger element (replaces default button)
 *   - default: Dropdown items (ft-dropdown-item elements)
 *
 * Events:
 *   - select: { value: string } (for slotted items)
 *   - change: { value: string } (when using options/value)
 *   - open: dropdown opened
 *   - close: dropdown closed
 *
 * Public API:
 *   - open(): void
 *   - close(): void
 *   - toggle(): void
 *   - setOptions(options: Option[]): void
 *   - getValue(): string | null
 *   - setValue(value: string): void
 */

interface DropdownOption {
  value: string;
  label: string;
}

class Dropdown extends HTMLElement {
  private abortController = new AbortController();
  private sheet: CSSStyleSheet | null = null;
  private isOpen = false;
  private focusedIndex = -1;
  private readonly instanceId = Math.random().toString(36).slice(2, 8);
  private options: DropdownOption[] = [];
  private selectedValue: string | null = null;

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
  }

  static get observedAttributes(): string[] {
    return ["label", "disabled", "align", "options", "value", "size"];
  }

  private get label(): string {
    if (this.options.length > 0 && this.selectedValue) {
      const opt = this.options.find(o => o.value === this.selectedValue);
      if (opt) return opt.label;
    }
    // Return explicit label or non-breaking space to maintain height
    return this.getAttribute("label") || "\u00A0";
  }

  private get size(): "small" | "normal" {
    return (this.getAttribute("size") as "small" | "normal") || "normal";
  }

  private get align(): "left" | "right" {
    return (this.getAttribute("align") as "left" | "right") || "left";
  }

  connectedCallback(): void {
    this.parseAttributes();
    this.render();
    this.setupEventListeners();
  }

  disconnectedCallback(): void {
    this.abortController.abort();
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return;
    if (!this.shadowRoot?.children.length) return;

    if (name === "options" || name === "value") {
      this.parseAttributes();
    }
    this.render();
  }

  private parseAttributes(): void {
    const optionsAttr = this.getAttribute("options");
    if (optionsAttr) {
      try {
        this.options = JSON.parse(optionsAttr);
      } catch {
        console.warn("ft-dropdown: invalid options JSON");
      }
    }
    this.selectedValue = this.getAttribute("value");
  }

  // Public API
  open(): void {
    if (this.hasAttribute("disabled")) return;
    this.isOpen = true;
    this.classList.add("open");
    this.focusedIndex = 0;
    this.updateFocus();
    this.dispatchEvent(new CustomEvent("open"));
  }

  close(): void {
    this.isOpen = false;
    this.classList.remove("open");
    this.focusedIndex = -1;
    this.updateFocus();
    this.dispatchEvent(new CustomEvent("close"));
  }

  toggle(): void {
    this.isOpen ? this.close() : this.open();
  }

  setOptions(options: DropdownOption[]): void {
    this.options = options;
    this.render();
    this.setupEventListeners();
  }

  getValue(): string | null {
    return this.selectedValue;
  }

  setValue(value: string): void {
    this.selectedValue = value;
    this.setAttribute("value", value);
    this.render();
  }

  private get items(): HTMLElement[] {
    return Array.from(this.querySelectorAll("ft-dropdown-item"));
  }

  private render(): void {
    if (!this.shadowRoot) return;

    // Rebuild stylesheet to handle dynamic size
    this.sheet = new CSSStyleSheet();
    this.sheet.replaceSync(this.getStyles());
    this.shadowRoot.adoptedStyleSheets = [this.sheet];

    const hasOptions = this.options.length > 0;
    const menuContent = hasOptions
      ? this.options.map((opt, i) => `
          <div
            class="item${opt.value === this.selectedValue ? " selected" : ""}${i === this.focusedIndex ? " focused" : ""}"
            role="menuitem"
            data-value="${opt.value}"
            data-index="${i}">
            ${opt.label}
          </div>
        `).join("")
      : "<slot></slot>";

    this.shadowRoot.innerHTML = `
      <div class="container">
        <slot name="trigger">
          <button
            class="trigger"
            type="button"
            aria-haspopup="menu"
            aria-expanded="${this.isOpen}"
            aria-controls="menu-${this.instanceId}"
            ${this.hasAttribute("disabled") ? "disabled" : ""}>
            <span class="label">${this.label}</span>
            <span class="chevron" aria-hidden="true">${icon('chevron-down', 14)}</span>
          </button>
        </slot>
        <div class="menu" id="menu-${this.instanceId}" role="menu">
          ${menuContent}
        </div>
      </div>
    `;
  }

  private getCustomTrigger(): HTMLElement | null {
    const slot = this.shadowRoot?.querySelector('slot[name="trigger"]') as HTMLSlotElement;
    return slot?.assignedElements()[0] as HTMLElement || null;
  }

  private updateFocus(): void {
    this.items.forEach((item, i) => {
      item.classList.toggle("focused", i === this.focusedIndex);
    });

    // Update ARIA on whichever trigger is active
    const trigger = this.getCustomTrigger() || this.shadowRoot?.querySelector(".trigger");
    trigger?.setAttribute("aria-expanded", String(this.isOpen));
  }

  private setupEventListeners(): void {
    this.abortController.abort();
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    document.addEventListener("click", this.handleOutsideClick, { signal });
    document.addEventListener("keydown", this.handleKeydown, { signal });
    this.shadowRoot?.addEventListener("click", this.handleShadowClick, { signal });
    this.addEventListener("click", this.handleSlotClick, { signal });

    // Set initial ARIA on custom trigger if present
    const customTrigger = this.getCustomTrigger();
    if (customTrigger) {
      customTrigger.setAttribute("aria-haspopup", "menu");
      customTrigger.setAttribute("aria-expanded", "false");
      customTrigger.setAttribute("aria-controls", `menu-${this.instanceId}`);
    }
  }

  private handleOutsideClick = (e: Event): void => {
    if (!e.composedPath().includes(this)) {
      this.close();
    }
  };

  private handleShadowClick = (e: Event): void => {
    const target = e.target as HTMLElement;

    // Handle default trigger button click
    if (target.closest(".trigger")) {
      this.toggle();
      return;
    }

    // Handle option items (when using options attribute)
    const item = target.closest(".item") as HTMLElement;
    if (item?.dataset.value !== undefined) {
      const value = item.dataset.value;
      this.selectedValue = value;
      this.setAttribute("value", value);
      this.close();
      this.render();
      this.setupEventListeners();
      this.dispatchEvent(new CustomEvent("change", {
        detail: { value },
        bubbles: true,
        composed: true,
      }));
    }
  };

  private handleSlotClick = (e: Event): void => {
    const target = e.target as HTMLElement;

    // Handle custom trigger click (slotted content is in light DOM)
    if (target.closest('[slot="trigger"]')) {
      e.stopPropagation();
      this.toggle();
      return;
    }

    // Handle dropdown item click
    const item = target.closest("ft-dropdown-item") as HTMLElement;
    if (item) {
      const value = item.getAttribute("value") || "";
      this.close();
      this.dispatchEvent(new CustomEvent("select", {
        detail: { value },
        bubbles: true,
        composed: true,
      }));
    }
  };

  private handleKeydown = (e: KeyboardEvent): void => {
    if (!this.isOpen && !this.contains(document.activeElement)) return;

    const items = this.items;

    switch (e.key) {
      case "Escape":
        if (this.isOpen) {
          e.preventDefault();
          this.close();
          this.shadowRoot?.querySelector<HTMLButtonElement>(".trigger")?.focus();
        }
        break;

      case "ArrowDown":
        e.preventDefault();
        if (!this.isOpen) {
          this.open();
        } else {
          this.focusedIndex = Math.min(this.focusedIndex + 1, items.length - 1);
          this.updateFocus();
        }
        break;

      case "ArrowUp":
        e.preventDefault();
        if (this.isOpen) {
          this.focusedIndex = Math.max(this.focusedIndex - 1, 0);
          this.updateFocus();
        }
        break;

      case "Enter":
      case " ":
        if (this.isOpen && this.focusedIndex >= 0) {
          e.preventDefault();
          const item = items[this.focusedIndex];
          if (item) {
            const value = item.getAttribute("value") || "";
            this.close();
            this.dispatchEvent(new CustomEvent("select", {
              detail: { value },
              bubbles: true,
              composed: true,
            }));
          }
        } else if (!this.isOpen && this.contains(document.activeElement)) {
          e.preventDefault();
          this.open();
        }
        break;

      case "Tab":
        if (this.isOpen) this.close();
        break;
    }
  };

  private getStyles(): string {
    const sizeStyles = this.size === "small"
      ? "height: var(--height-sm, 32px); padding: 0 10px; font-size: 12px;"
      : "height: var(--height-md, 40px); padding: 0 12px; font-size: 14px;";

    const itemSizeStyles = this.size === "small"
      ? "padding: 6px 10px; font-size: 12px;"
      : "padding: 8px 12px; font-size: 14px;";

    return `
      :host {
        display: inline-block;
        position: relative;
        font-family: system-ui, -apple-system, sans-serif;
      }

      .container {
        position: relative;
      }

      .trigger {
        display: flex;
        align-items: center;
        gap: 8px;
        ${sizeStyles}
        min-width: var(--dropdown-min-width, auto);
        font-weight: 500;
        cursor: pointer;
        text-align: left;
        border-radius: var(--radius, 6px);
        color: var(--text, #1C1C1E);
        background: var(--input-bg, #ffffff);
        border: 1px solid var(--input-border, #E2E4E8);
        transition: border-color 150ms, box-shadow 150ms;
      }

      .trigger:hover:not(:disabled) {
        border-color: var(--input-border-focus, #999999);
      }

      .trigger:focus {
        outline: none;
        border-color: var(--input-border-focus, #999999);
        box-shadow: 0 0 0 2px var(--focus-ring, rgba(0, 0, 0, 0.1));
      }

      .trigger:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      .label {
        flex: 1;
      }

      .chevron {
        display: flex;
        flex-shrink: 0;
        opacity: 0.6;
        transition: transform 150ms;
      }

      :host(.open) .chevron {
        transform: rotate(180deg);
      }

      .menu {
        position: absolute;
        top: calc(100% + 4px);
        ${this.align === "right" ? "right: 0;" : "left: 0;"}
        min-width: 100%;
        max-height: 240px;
        overflow-y: auto;
        opacity: 0;
        visibility: hidden;
        transform: translateY(-4px);
        transition: opacity 150ms, transform 150ms, visibility 150ms;
        z-index: 100;
        border-radius: var(--radius, 6px);
        background: var(--input-bg, #ffffff);
        border: 1px solid var(--input-border, #E2E4E8);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      }

      :host(.open) .menu {
        opacity: 1;
        visibility: visible;
        transform: translateY(0);
      }

      .item {
        ${itemSizeStyles}
        cursor: pointer;
        color: var(--text, #1C1C1E);
        transition: background 100ms;
      }

      .item:first-child { border-radius: 5px 5px 0 0; }
      .item:last-child { border-radius: 0 0 5px 5px; }
      .item:only-child { border-radius: 5px; }

      .item:hover,
      .item.focused {
        background: var(--tab-bg, #f4f4f5);
      }

      .item.focused {
        background: var(--tab-bg-hover, #e9e9eb);
      }

      .item.selected {
        color: var(--primary, #e0524d);
        font-weight: 600;
      }

      @media (prefers-color-scheme: dark) {
        .trigger {
          background: var(--input-bg, #14161a);
          border-color: var(--input-border, #272a31);
          color: var(--text, #ffffff);
        }

        .menu {
          background: var(--input-bg, #14161a);
          border-color: var(--input-border, #272a31);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
        }

        .item {
          color: var(--text, #ffffff);
        }

        .item:hover,
        .item.focused {
          background: var(--tab-bg, #1f2328);
        }

        .item.focused {
          background: var(--tab-bg-hover, #2a2f36);
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .chevron, .menu, .item { transition: none; }
      }
    `;
  }
}

class DropdownItem extends HTMLElement {
  private sheet: CSSStyleSheet | null = null;

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
  }

  connectedCallback(): void {
    this.render();
    this.setAttribute("role", "menuitem");
    this.setAttribute("tabindex", "-1");
  }

  private render(): void {
    if (!this.shadowRoot) return;

    if (!this.sheet) {
      this.sheet = new CSSStyleSheet();
      this.sheet.replaceSync(this.getStyles());
      this.shadowRoot.adoptedStyleSheets = [this.sheet];
    }

    this.shadowRoot.innerHTML = `<slot></slot>`;
  }

  private getStyles(): string {
    return `
      :host {
        display: block;
        padding: 8px 12px;
        font-size: 14px;
        cursor: pointer;
        color: var(--text, #1C1C1E);
        transition: background 100ms;
      }

      :host(:first-child) { border-radius: 5px 5px 0 0; }
      :host(:last-child) { border-radius: 0 0 5px 5px; }
      :host(:only-child) { border-radius: 5px; }

      :host(:hover),
      :host(.focused) {
        background: var(--tab-bg, #f4f4f5);
      }

      :host(.focused) {
        background: var(--tab-bg-hover, #e9e9eb);
      }

      @media (prefers-color-scheme: dark) {
        :host {
          color: var(--text, #ffffff);
        }

        :host(:hover),
        :host(.focused) {
          background: var(--tab-bg, #1f2328);
        }

        :host(.focused) {
          background: var(--tab-bg-hover, #2a2f36);
        }
      }

      @media (prefers-reduced-motion: reduce) {
        :host { transition: none; }
      }
    `;
  }
}

if (!customElements.get("ft-dropdown")) {
  customElements.define("ft-dropdown", Dropdown);
}

if (!customElements.get("ft-dropdown-item")) {
  customElements.define("ft-dropdown-item", DropdownItem);
}
